// src/utils/api.ts
import axios from 'axios';

// Smart API URL detection based on current domain
const getApiUrl = () => {
  // Allow override via env var (used for staging builds)
  if (process.env.REACT_APP_API_URL !== undefined) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV !== 'production') {
    return ''; // Use proxy in development
  }
  return 'https://ascension-ai-backend-hnhtbah0dwahcud3.eastus2-01.azurewebsites.net';
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache'
  },
  withCredentials: true,
  timeout: 30000,
});

// Track refresh state to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let isRedirecting = false;

// Add response interceptor with token refresh logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried and not the refresh/login endpoint itself
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/refresh') &&
      !originalRequest.url?.includes('/api/login') &&
      !originalRequest.url?.includes('/api/logout')
    ) {
      originalRequest._retry = true;

      // If already refreshing, wait for the existing refresh to complete
      if (isRefreshing && refreshPromise) {
        try {
          await refreshPromise;
          return api(originalRequest);
        } catch {
          // Refresh failed, fall through to redirect
        }
      }

      // Attempt token refresh
      isRefreshing = true;
      refreshPromise = axios.post(
        `${API_URL}/api/refresh`,
        {},
        { withCredentials: true }
      );

      try {
        await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;
        // Retry the original request with the new token (cookie is set automatically)
        return api(originalRequest);
      } catch {
        // Refresh failed - token is fully expired, redirect to login
        isRefreshing = false;
        refreshPromise = null;
      }
    }

    // Handle 503 maintenance mode - show maintenance page
    if (error.response?.status === 503 && error.response?.data?.error === 'maintenance') {
      if (!document.getElementById('maintenance-overlay')) {
        const overlay = document.createElement('div');
        overlay.id = 'maintenance-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:#16161A;display:flex;align-items:center;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;';
        overlay.innerHTML = `<div style="text-align:center;max-width:500px;padding:40px 24px;">
          <div style="font-size:48px;margin-bottom:24px;">&#9874;</div>
          <h1 style="color:#6F9F95;font-size:28px;margin-bottom:16px;">Scheduled Maintenance</h1>
          <div style="width:60px;height:3px;background:#6F9F95;margin:24px auto;border-radius:2px;"></div>
          <p style="color:#A0A0A4;font-size:16px;line-height:1.6;margin-bottom:12px;">MedNecessity is currently undergoing a <span style="color:#fff;font-weight:600;">platform upgrade</span>.</p>
          <p style="color:#A0A0A4;font-size:16px;line-height:1.6;">We will be back online <span style="color:#fff;font-weight:600;">Monday morning</span>.</p>
          <p style="color:#A0A0A4;font-size:14px;margin-top:24px;">If you need immediate assistance, please contact your account representative.</p>
        </div>`;
        document.body.appendChild(overlay);
      }
      return Promise.reject(error);
    }

    // Handle 401 that can't be refreshed - redirect to login
    if (error.response?.status === 401 && !isRedirecting) {
      isRedirecting = true;
      localStorage.removeItem('user');

      setTimeout(() => {
        if (!window.location.pathname.includes('/login') && window.location.pathname !== '/') {
          window.location.href = '/login';
        }
        isRedirecting = false;
      }, 500);
    }

    return Promise.reject(error);
  }
);

export default api;
